import { reactive, readonly } from 'vue';
import { ApiFailure, createClient } from '../api/client';
import type { LocaleOption, MePayload, Prerequisites } from '../api/types';
import { i18n, resolveLocale, supportedUiLocales, type UiLocale } from '../i18n';

/**
 * 全站共享状态（★ 不引 Pinia：迭代一只有"语言清单 + 登录态"两份状态，
 * 引一个状态库换来的构建产物与心智成本比它省下的多）。
 */
const TOKEN_KEY = 'platform.web.accessToken';
const REFRESH_KEY = 'platform.web.refreshToken';

function storage(): Storage | null {
  return typeof localStorage === 'undefined' ? null : localStorage;
}

interface AppState {
  locales: LocaleOption[];
  uiLocales: UiLocale[];
  locale: UiLocale;
  prerequisites: Prerequisites | null;
  loadError: ApiFailure | null;
  ready: boolean;
  token: string | null;
  me: MePayload | null;
}

const state = reactive<AppState>({
  locales: [],
  uiLocales: [],
  locale: 'zh',
  prerequisites: null,
  loadError: null,
  ready: false,
  token: storage()?.getItem(TOKEN_KEY) ?? null,
  me: null,
});

export const api = createClient({ token: () => state.token });

export function useApp() {
  /** 语言清单 = locales 表 ∩ 已打包 UI（A11）；顺带拉一次前置要求（A1 披露文案的数据源） */
  async function bootstrap(): Promise<void> {
    if (state.ready) return;
    try {
      const [loc, pre] = await Promise.all([api.locales(), api.prerequisites()]);
      state.locales = loc.items;
      state.uiLocales = supportedUiLocales(loc.items.map((l) => l.code));
      // ★ prerequisites 是单对象接口（不是列表）—— 直接赋值，绝不取 items[0]
      state.prerequisites = pre;
      state.loadError = null;
    } catch (e) {
      // ★ 语言清单拉不到时也必须能渲染：退化成"已打包 UI"，否则首屏直接白屏
      state.uiLocales = state.uiLocales.length > 0 ? state.uiLocales : supportedUiLocales([]);
      state.loadError = e instanceof ApiFailure ? e : new ApiFailure('UNKNOWN', 500);
    } finally {
      state.ready = true;
    }
    await restoreSession();
  }

  /** 路由守卫用：把 URL 上的 :locale 落到界面语言；未知语言返回 false 让上层跳转 */
  function applyLocale(param: string | undefined): boolean {
    const next = resolveLocale(
      param,
      state.uiLocales,
      typeof navigator === 'undefined' ? undefined : navigator.language,
    );
    if (!next) return false;
    state.locale = next;
    i18n.global.locale.value = next;
    if (typeof document !== 'undefined') document.documentElement.lang = next === 'zh' ? 'zh-CN' : next;
    return true;
  }

  function setToken(token: string | null, refresh?: string | null): void {
    state.token = token;
    const s = storage();
    if (!s) return;
    if (token) s.setItem(TOKEN_KEY, token);
    else s.removeItem(TOKEN_KEY);
    if (refresh !== undefined) {
      if (refresh) s.setItem(REFRESH_KEY, refresh);
      else s.removeItem(REFRESH_KEY);
    }
  }

  async function signIn(email: string, password: string): Promise<void> {
    // ★ login 是单对象接口：拿到会话对象本身，不是 items[0]
    const session = await api.login(email, password);
    if (!session?.accessToken) throw new ApiFailure('EMPTY_SESSION', 500);
    setToken(session.accessToken, session.refreshToken);
    await restoreSession();
  }

  /** 注册后 dev 环境直接回显 verifyToken ⇒ 顺手把邮箱置为已验证再登录（生产走邮件，不在这条路上） */
  async function signUp(email: string, password: string): Promise<void> {
    const r = await api.register(email, password);
    const verifyToken = r.verifyToken;
    // ★ 验证失败不能阻断登录：production 不返回 verifyToken，这条路本来就走不到
    if (verifyToken) await api.verifyEmail(verifyToken).catch(() => undefined);
    await signIn(email, password);
  }

  async function restoreSession(): Promise<void> {
    if (!state.token) {
      state.me = null;
      return;
    }
    try {
      // ★ me 是单对象接口
      state.me = await api.me();
    } catch (e) {
      if (e instanceof ApiFailure && (e.status === 401 || e.status === 403)) setToken(null, null);
    }
  }

  function signOut(): void {
    setToken(null, null);
    state.me = null;
  }

  return {
    state: readonly(state),
    api,
    bootstrap,
    applyLocale,
    signIn,
    signUp,
    signOut,
  };
}

export type AppStore = ReturnType<typeof useApp>;

/** 单例：视图层通过 provide/inject 之外的最简方式共享同一份状态 */
export const appStore = useApp();
